import loadModule from '@utils/loadModule';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useFlagSelector } from '@hooks/useLayoutOptions';
import { useSelector } from 'react-redux';

const setupIntercom = (
  me,
  update = false,
  meUid,
  hideIntercom,
  isSubscribed,
  dispatch,
  freshdeskEnabled
) => {
  loadModule(import('@utils/intercomHelper')).then((res) => {
    const intercomHelper = res.default;
    intercomHelper(
      meUid,
      me.name,
      me.email,
      me.uid,
      me.isEducator,
      hideIntercom,
      isSubscribed,
      me.isSchoolEducator,
      dispatch,
      update,
      freshdeskEnabled
    );
  });
};

const useIntercom = (
  route,
  meUid,
  prevUserId,
  isClassModalOpened,
  me,
  isSubscribed,
  dispatch,
  isLoggedIn
) => {
  const router = useRouter();
  const { inside_app } = router.query;
  const timerRef = useRef(null);
  const hideIntercom = useFlagSelector('hideIntercom');

  const freshdeskEnabled = useSelector(
    ({ auth }) => auth.accessControl.has_freshdesk_enabled
  );

  useEffect(() => {
    if (meUid) {
      if (
        (me.isEducator && !process.env.INTERCOM_EDUCATOR_ID) ||
        (!me.isEducator &&
          isSubscribed &&
          !process.env.INTERCOM_USER_SUBSCRIBED_ID) ||
        (me.isSchoolEducator && !process.env.INTERCOM_SCHOOL_EDUCATOR_ID)
      )
        return;
    } else if (!process.env.INTERCOM_USER_LOGGED_OUT_ID) return;
    if (!me.isEducator) return;
    if (!hideIntercom && !window.Intercom && !inside_app) {
      timerRef.current = setTimeout(
        () =>
          setupIntercom(
            me,
            false,
            meUid,
            hideIntercom,
            isSubscribed,
            dispatch,
            freshdeskEnabled
          ),
        10 * 1000
      );
    }

    if (hideIntercom && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (meUid !== prevUserId && !hideIntercom && !inside_app) {
      if (window.Intercom) {
        // if intercom already loaded just update the configs
        setupIntercom(
          me,
          true,
          meUid,
          hideIntercom,
          isSubscribed,
          dispatch,
          freshdeskEnabled
        );
      } else if (!timerRef.current) {
        // start intercom with 10s delay bcz if user changed the route to non intercom route we can clear the load itself
        timerRef.current = setTimeout(
          () =>
            setupIntercom(
              me,
              false,
              meUid,
              hideIntercom,
              isSubscribed,
              dispatch,
              freshdeskEnabled
            ),
          10 * 1000
        );
      }
    }

    const interval = setInterval(() => {
      // checks and hides intercom once loaded on the basis of route, classmodal if anyone of the two changes

      const intercomContainer = document.getElementById('intercom-container');
      if (intercomContainer) {
        if (!hideIntercom && !isClassModalOpened && me.isEducator) {
          intercomContainer.style.display = 'block';
        } else if (hideIntercom || isClassModalOpened || isSubscribed) {
          // hide intercom if loaded and the user is on non intercom route
          intercomContainer.style.display = 'none';
          // window.Intercom('shutdown');
        } else {
          // show intercom if loaded and the user is on intercom route
          intercomContainer.style.display = 'block';
          // window.Intercom('update');
        }
        // and clear the interval check
        clearInterval(interval);
      }
      if (!window.Intercom) {
        clearInterval(interval);
      }
    }, 1000);

    const plusEducatorInterval = setInterval(() => {
      if (window.Intercom && freshdeskEnabled) {
        window.Intercom('onUnreadCountChange', (unreadCount) => {
          if (unreadCount) {
            window.Intercom('update', {
              ...window.intercomSettings,
              hide_default_launcher: false
            });
            window.Intercom('showMessages');
            clearInterval(plusEducatorInterval);
          }
        });
      } else if (window.Intercom) clearInterval(plusEducatorInterval);
    }, 11000);

    return () => {
      clearInterval(interval);
      clearInterval(plusEducatorInterval);
      if (hideIntercom) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    route,
    meUid,
    prevUserId,
    isClassModalOpened,
    me,
    isSubscribed,
    dispatch,
    isLoggedIn,
    inside_app,
    hideIntercom,
    freshdeskEnabled
  ]);
};

export default useIntercom;
